import { Component, Input, OnChanges } from '@angular/core';

interface RevenuePaymentAmount {
  idDocumento: string;
  importoAssegnato: number;
  nuovoIncassoAttivo: boolean;
}

@Component({
  selector: 'rev-receipts-payment-amount-cell',
  template: `
    <label
      class="amount-field"
      [class.amount-field-disabled]="!itemData.nuovoIncassoAttivo"
      (click)="$event.stopPropagation()"
    >
      <span aria-hidden="true">€</span>
      <input
        type="text"
        inputmode="decimal"
        [value]="displayValue"
        [disabled]="!itemData.nuovoIncassoAttivo"
        [attr.aria-label]="'Importo assegnato per ' + itemData.idDocumento"
        (focus)="prepareInput($event)"
        (input)="updateAmount($event)"
        (blur)="formatAmount()"
        (keydown)="validateKey($event)"
      />
    </label>
  `,
  styles: `
    :host {
      display: flex;
      justify-content: flex-end;
    }

    .amount-field {
      display: flex;
      align-items: center;
      gap: var(--mat-sys-spacing-4);
      width: 128px;
      height: 32px;
      padding: 0 var(--mat-sys-spacing-8);
      border: 1px solid var(--mat-sys-primary);
      border-radius: var(--mat-sys-corner-medium);
      color: var(--mat-sys-primary);
      background: var(--mat-sys-surface);
      box-sizing: border-box;
    }

    .amount-field-disabled {
      border-color: color-mix(in srgb, var(--mat-sys-on-surface) 12%, transparent);
      color: color-mix(in srgb, var(--mat-sys-on-surface) 38%, transparent);
      background-color: color-mix(in srgb, var(--mat-sys-on-surface) 4%, var(--mat-sys-surface));
      cursor: not-allowed;
    }

    input {
      width: 100%;
      min-width: 0;
      padding: 0;
      border: 0;
      outline: 0;
      color: inherit;
      background: transparent;
      font: inherit;
      text-align: right;
    }

    input:disabled {
      cursor: not-allowed;
    }
  `,
})
export class receiptsPaymentAmountCell implements OnChanges {
  @Input({ required: true }) itemData!: RevenuePaymentAmount;
  @Input() infoColumn: unknown;
  @Input() _reloadData?: { emit: () => void };

  displayValue = '0,00';

  ngOnChanges(): void {
    this.formatAmount();
  }

  prepareInput(event: FocusEvent): void {
    if (this.itemData.importoAssegnato !== 0) {
      return;
    }

    this.displayValue = '';
    (event.target as HTMLInputElement).value = '';
  }

  validateKey(event: KeyboardEvent): void {
    event.stopPropagation();

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Enter',
      'ArrowLeft', 'ArrowRight', 'Home', 'End',
    ];

    if (allowedKeys.includes(event.key) || /^\d$/.test(event.key)) {
      return;
    }

    if ((event.key === ',' || event.key === '.') && !this.displayValue.includes(',')) {
      return;
    }

    event.preventDefault();
  }

  updateAmount(event: Event): void {
    const input = event.target as HTMLInputElement;
    const normalizedValue = input.value.replace('.', ',');
    const [integerPart, ...decimalParts] = normalizedValue.split(',');
    const sanitizedValue = integerPart.replace(/\D/g, '')
      + (decimalParts.length ? `,${decimalParts.join('').replace(/\D/g, '')}` : '');

    this.displayValue = sanitizedValue;
    input.value = sanitizedValue;

    if (!sanitizedValue) {
      this.itemData.importoAssegnato = 0;
      this._reloadData?.emit();
      return;
    }

    const amount = Number(sanitizedValue.replace(',', '.'));

    if (Number.isFinite(amount) && amount >= 0) {
      this.itemData.importoAssegnato = amount;
      this._reloadData?.emit();
    }
  }

  formatAmount(): void {
    const amount = this.itemData?.importoAssegnato ?? 0;
    this.displayValue = amount.toFixed(2).replace('.', ',');
  }
}
